import { trackCatalogEvent } from '~/modules/catalogEventTracking.js'

export const CHRISTMAS_MARKET_ANALYTICS_EVENTS = Object.freeze([
  'xmas_pricing_view',
  'xmas_price_calculated',
  'xmas_checkout_started',
  'xmas_purchase'
])

const SAFE_PROPERTY_KEYS = Object.freeze(['area_tier', 'event_days', 'net_price', 'pricing_version', 'package_key'])
const PRICING_VERSION_FORMAT = /^christmas_market_[0-9]{4}$/
const PACKAGE_KEY_FORMAT = /^(four_hour|six_hour)$/

export function trackChristmasMarketEvent(eventName, properties = {}, { documentRef = document } = {}) {
  if (!CHRISTMAS_MARKET_ANALYTICS_EVENTS.includes(eventName)) {
    return false
  }

  if (!trackingConsentGranted(documentRef)) {
    return false
  }

  try {
    return trackCatalogEvent(eventName, normalizeProperties(properties))
  } catch (_error) {
    return false
  }
}

export function trackChristmasMarketPricingView(properties = {}, options = {}) {
  return trackChristmasMarketEvent('xmas_pricing_view', properties, options)
}

export function trackChristmasMarketPriceCalculated(properties, options = {}) {
  return trackChristmasMarketEvent('xmas_price_calculated', properties, options)
}

export function trackChristmasMarketCheckoutStarted(properties, options = {}) {
  return trackChristmasMarketEvent('xmas_checkout_started', properties, options)
}

export function trackChristmasMarketPurchase(properties, options = {}) {
  return trackChristmasMarketEvent('xmas_purchase', properties, options)
}

export function trackingConsentGranted(documentRef = document) {
  if (!documentRef || typeof documentRef.cookie !== 'string') {
    return false
  }

  const rawCookie = documentRef.cookie
    .split(';')
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith('cookie_consent_level='))
    ?.slice('cookie_consent_level='.length)

  if (!rawCookie) {
    return false
  }

  try {
    const consentLevels = JSON.parse(decodeURIComponent(rawCookie.replace(/\+/g, ' ')))
    return consentLevels && consentLevels.tracking === true
  } catch (_error) {
    return false
  }
}

function normalizeProperties(properties) {
  const source = properties && typeof properties === 'object' ? properties : {}
  const normalized = {}

  SAFE_PROPERTY_KEYS.forEach((key) => {
    const value = source[key]
    if (value === undefined || value === null || value === '') {
      return
    }

    if (key === 'area_tier' || key === 'event_days') {
      const number = Number(value)
      if (Number.isInteger(number) && number > 0) {
        normalized[key] = number
      }
      return
    }

    if (key === 'net_price') {
      const number = Number(value)
      if (Number.isFinite(number) && number >= 0) {
        normalized[key] = number
      }
      return
    }

    if (key === 'package_key') {
      const packageKey = String(value).trim()
      if (packageKey.match(PACKAGE_KEY_FORMAT)) {
        normalized[key] = packageKey
      }
      return
    }

    const pricingVersion = String(value).trim()
    if (pricingVersion.match(PRICING_VERSION_FORMAT)) {
      normalized[key] = pricingVersion
    }
  })

  return normalized
}
